import {
  queryOptions,
  useMutation,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'
import { api } from './client'

/**
 * Matches domain — list (cross-loop + per-loop), detail, apply, mark-applied.
 *
 * `applyPath` collapses LoopCV's supportedApplyMethods zoo into 3
 * frontend-friendly states (backend/internal/loopcv/types.go):
 *
 *   auto   — backend can fire POST /matches/:id/apply (dameron only today).
 *   manual — has an applyLink the user can click out to.
 *   none   — no path exposed by LoopCV; nothing we can do automatically.
 *
 * `status` is our canonical bucket over LoopCV's 15-value upstream enum
 * (see partner-routes-official-v2 §4.4). The backend does the translation;
 * the UI just renders.
 *
 * `appliedAt` is our own truth (the match_applications table), set on
 * Apply() success OR MarkApplied() — both surface as "applied" in UI.
 */

export type ApplyPath = 'auto' | 'manual' | 'none'

export type MatchStatus =
  | 'ready' // ONLY_MATCH, ONLY_MATCH_WITH_FORM
  | 'pending' // PENDING (apply queued, LoopCV bot working)
  | 'applied' // FORM_APPLIED, SHADOW_APPLIED, SENT, ALREADY_APPLIED, ALREADY_SENT, MANUALLY_LOGGED_APPLICATION
  | 'easy_apply' // EASY_APPLY (needs Chrome extension)
  | 'needs_questions' // FORM_HAS_QUESTIONS (screening Qs before apply)
  | 'no_apply_method' // NO_AVAILABLE_APPLICATION_METHOD
  | 'closed' // CLOSED (posting expired/removed)
  | 'failed' // FAILED
  | 'unknown'

interface SupportedApplyMethods {
  application: 'extension' | 'dameron' | ''
  email: boolean
}

/** Canonical control kind, mirrored from the backend's QuestionType. */
export type QuestionKind = 'text' | 'boolean' | 'number' | 'single_select' | 'multi_select'

export interface Question {
  id: string
  label: string
  type: string
  /** Server-canonical kind. Present on the dedicated /questions endpoint;
   * absent on questions embedded in the match-detail payload. */
  kind?: QuestionKind
  options: string[]
}

/** A value submitted back to LoopCV, typed per question kind. */
export type AnswerValue = string | number | boolean | string[]

/**
 * resolveQuestionKind trusts the server `kind` when present and otherwise
 * falls back to an options heuristic. The dedicated /questions endpoint
 * (the normal Q&A path) always sets `kind`; the fallback only fires for
 * detail-embedded questions, which the panel rarely renders.
 */
export function resolveQuestionKind(q: Question): QuestionKind {
  if (q.kind) return q.kind
  return q.options.length > 0 ? 'single_select' : 'text'
}

export interface Match {
  id: string
  loopId: string
  platform: string
  applyLink: string
  jobTitle: string
  company: string
  jobLocation: string
  status: MatchStatus
  reason?: string
  matchingReason?: string
  supportedApplyMethods: SupportedApplyMethods
  applyPath: ApplyPath
  /** Top-level mirror of supportedApplyMethods.application === "extension". */
  easyApply: boolean
  /** LoopCV flag: posting is no longer fresh. UI may grey it out. */
  isOutdated: boolean
  salary?: string
  remote?: boolean | null
  hybrid?: boolean | null
  logo?: string
  matchCreatedAt: string
  appliedAt?: string | null

  // Detail-only fields. Nil on list responses.
  jobSummary?: string
  jobSummaryHtml?: string
  matchingScore?: number | null
  keywords?: string[]
  questions?: Question[]
  domain?: string
}

export interface MatchPage {
  items: Match[]
  total: number
  page: number
  perPage: number
}

/* -------------------------------------------------------------------------
 * Server-side filter contract (P1.6)
 * ------------------------------------------------------------------------- */

/**
 * Raw LoopCV match-status enum (partner-routes-official-v2 §4.4). The
 * backend accepts CSVs of these on `/api/matches?filter=…` and maps to
 * our canonical `MatchStatus` buckets when returning rows. We pass
 * upstream values (not bucket names) so the frontend can request finer
 * slices than a bucket allows.
 */
export type UpstreamStatus =
  | 'ONLY_MATCH'
  | 'ONLY_MATCH_WITH_FORM'
  | 'FORM_APPLIED'
  | 'SHADOW_APPLIED'
  | 'MANUALLY_LOGGED_APPLICATION'
  | 'SENT'
  | 'FORM_HAS_QUESTIONS'
  | 'EASY_APPLY'
  | 'ALREADY_APPLIED'
  | 'ALREADY_SENT'
  | 'NO_AVAILABLE_APPLICATION_METHOD'
  | 'PENDING'
  | 'FAILED'
  | 'CLOSED'

/**
 * The six upstream values that collapse to our `applied` bucket. Mirrors
 * the canonical mapping in MatchStatus above — keep in sync if backend
 * adds new applied-ish statuses.
 */
export const APPLIED_STATUSES: UpstreamStatus[] = [
  'FORM_APPLIED',
  'SHADOW_APPLIED',
  'SENT',
  'ALREADY_APPLIED',
  'ALREADY_SENT',
  'MANUALLY_LOGGED_APPLICATION',
]

export interface MatchListFilters {
  /** Upstream status values; canonical bucket name on backend is mapped through enums. */
  status?: UpstreamStatus[]
  companies?: string[]
  easyApply?: boolean
  platforms?: string[]
  keywords?: string[]
  matchingReasons?: string[]
}

/** Stable JSON shape for cache keys — array order changes must not split caches. */
function normalizeFilters(filters?: MatchListFilters) {
  if (!filters) return undefined
  const sortedArr = (a?: string[]) =>
    a && a.length > 0 ? [...a].sort() : undefined
  const out = {
    status: sortedArr(filters.status as string[] | undefined),
    companies: sortedArr(filters.companies),
    easyApply: filters.easyApply === true ? true : undefined,
    platforms: sortedArr(filters.platforms),
    keywords: sortedArr(filters.keywords),
    matchingReasons: sortedArr(filters.matchingReasons),
  }
  // Drop undefined keys so the object collapses to {} when no filter is set.
  const cleaned: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(out)) if (v !== undefined) cleaned[k] = v
  return Object.keys(cleaned).length > 0 ? cleaned : undefined
}

/** Serialize filters into URLSearchParams; empty/undefined arrays are omitted. */
function appendFilterParams(p: URLSearchParams, filters?: MatchListFilters) {
  if (!filters) return
  const csv = (a?: string[]) => (a && a.length > 0 ? a.join(',') : null)
  const status = csv(filters.status as string[] | undefined)
  if (status) p.set('filter', status)
  const companies = csv(filters.companies)
  if (companies) p.set('companyFilter', companies)
  if (filters.easyApply === true) p.set('easyApply', 'true')
  const platforms = csv(filters.platforms)
  if (platforms) p.set('platform', platforms)
  const keywords = csv(filters.keywords)
  if (keywords) p.set('keyword', keywords)
  const reasons = csv(filters.matchingReasons)
  if (reasons) p.set('matchingReason', reasons)
}

/* -------------------------------------------------------------------------
 * Query keys + factories
 * ------------------------------------------------------------------------- */

export const matchesKeys = {
  all: ['matches'] as const,
  cross: (page: number, perPage: number, filters?: MatchListFilters) =>
    [
      ...matchesKeys.all,
      'cross',
      { page, perPage, filters: normalizeFilters(filters) },
    ] as const,
  detail: (id: string) => [...matchesKeys.all, 'detail', id] as const,
}

function buildListUrl(base: string, page: number, perPage: number, filters?: MatchListFilters) {
  const p = new URLSearchParams()
  p.set('page', String(page))
  p.set('perPage', String(perPage))
  appendFilterParams(p, filters)
  return `${base}?${p.toString()}`
}

export const matchesListQuery = (
  page = 1,
  perPage = 50,
  filters?: MatchListFilters,
) =>
  queryOptions({
    queryKey: matchesKeys.cross(page, perPage, filters),
    queryFn: () => api.get<MatchPage>(buildListUrl('/matches', page, perPage, filters)),
  })

/** Match detail. Optional `poll` enables 10s refetch while drawer is open. */
export const matchQuery = (id: string, opts?: { poll?: boolean }) =>
  queryOptions({
    queryKey: matchesKeys.detail(id),
    queryFn: () => api.get<Match>(`/matches/${id}`),
    enabled: id.length > 0,
    refetchInterval: opts?.poll ? 10_000 : false,
  })

/* -------------------------------------------------------------------------
 * Mutations
 * ------------------------------------------------------------------------- */

export function useApplyMatchMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (matchId: string) => api.post<Match>(`/matches/${matchId}/apply`),
    onSuccess: (_match, matchId) => {
      void qc.invalidateQueries({ queryKey: matchesKeys.detail(matchId) })
      void invalidateMatchLists(qc)
    },
  })
}

export function useMarkAppliedMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (matchId: string) => api.post<Match>(`/matches/${matchId}/mark-applied`),
    onSuccess: (_match, matchId) => {
      void qc.invalidateQueries({ queryKey: matchesKeys.detail(matchId) })
      void invalidateMatchLists(qc)
    },
  })
}

function invalidateMatchLists(qc: QueryClient) {
  // Invalidate list keys but NOT detail keys (we set detail above explicitly).
  return Promise.all([
    qc.invalidateQueries({ queryKey: [...matchesKeys.all, 'cross'] }),
    qc.invalidateQueries({ queryKey: [...matchesKeys.all, 'byLoop'] }),
  ])
}

/* -------------------------------------------------------------------------
 * UI helpers — labels & classifications
 * ------------------------------------------------------------------------- */

export function applyPathLabel(path: ApplyPath): string {
  switch (path) {
    case 'auto':
      return 'Auto'
    case 'manual':
      return 'Manual'
    case 'none':
      return 'No path'
  }
}

export function applyPathDescription(path: ApplyPath): string {
  switch (path) {
    case 'auto':
      return 'We open the application and pull any screening questions for you to review before anything is sent.'
    case 'manual':
      return 'Click through to apply on the company site; we can generate a tailored cover letter.'
    case 'none':
      return 'No application link exposed. Track manually after applying elsewhere.'
  }
}

/** Map a status to a Badge variant. */
export function statusVariant(
  status: MatchStatus,
): 'success' | 'warning' | 'danger' | 'info' | 'neutral' {
  switch (status) {
    case 'applied':
      return 'success'
    case 'ready':
    case 'easy_apply':
      return 'info'
    case 'pending':
    case 'needs_questions':
      return 'warning'
    case 'failed':
      return 'danger'
    case 'no_apply_method':
    case 'closed':
      return 'warning'
    default:
      return 'neutral'
  }
}

export function statusLabel(status: MatchStatus): string {
  switch (status) {
    case 'ready':
      return 'Ready'
    case 'pending':
      return 'Applying…'
    case 'applied':
      return 'Applied'
    case 'easy_apply':
      return 'Easy Apply'
    case 'needs_questions':
      return 'Needs answers'
    case 'no_apply_method':
      return 'No apply method'
    case 'closed':
      return 'Closed'
    case 'failed':
      return 'Failed'
    default:
      return 'Unknown'
  }
}
